import { useRef } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { Alert, ModalButtonWithForm } from 'components/common';
import { Box } from 'components/common/box';
import { Checkbox, FormGroup, Text } from 'components/common/form';

import type { FormApi } from 'informed';

type FormState = Record<'title', string> & Record<'copyAllOwners', boolean>;

const PathCopy = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { path }
  } = useReduxState();

  const formApi = useRef<FormApi<FormState>>(undefined);

  const router = useRouter();

  const handleSubmit = async (values: FormState) => {
    try {
      const response = await api.post(`/admin/paths/${path.id}/copy`, {
        title: values.title,
        copyAllOwners: values.copyAllOwners
      });

      if (response.data?.success) {
        dispatch(
          actions.addNotice({
            text: messages.pathsCopySuccess,
            type: 'success'
          })
        );
        router.push(`/admin/paths/${response.data.path}`);
      } else {
        throw new Error('Something went wrong...');
      }
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const validateTitle = async (value: string) => {
    if (!value) return messages.commonTitleEmpty;

    try {
      await api.head(`/admin/paths?title=${value}`);
      return messages.commonTitleInUse;
    } catch (err) {
      return undefined;
    }
  };

  return (
    <Box title={messages.pathsCopy}>
      <Alert style={{ marginBottom: 10 }} text={messages.pathsCopyInfo} />

      <ModalButtonWithForm
        buttonIcon="copy"
        buttonText={messages.commonCopy}
        buttonCategory="success"
        modalActionText={messages.commonConfirm}
        modalTitle={messages.pathsCopy}
        onSubmit={handleSubmit}
        pristineIsValid={false}
        setFormApi={(api: FormApi<FormState>) => (formApi.current = api)}
      >
        {() => (
          <>
            <FormGroup label={messages.commonTitle}>
              <Text
                customValidateAsync={validateTitle}
                field="title"
                id="title"
                required={true}
              />
            </FormGroup>

            <FormGroup label={messages.commonCopyOwners}>
              <Checkbox
                field="copyAllOwners"
                id="copyAllOwners"
                label={messages.pathsCopyOwnersInfo}
              />
            </FormGroup>
          </>
        )}
      </ModalButtonWithForm>
    </Box>
  );
};

export default PathCopy;
